import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import { docLimitRefusal, type MusicVideoDocLimit } from './docLimits';

/** The most steps that Undo keeps. */
export const MUSIC_VIDEO_UNDO_STEPS = 100;
/** Changes with the same `coalesce` key inside this time are one Undo step (a drag, a slider). */
export const MUSIC_VIDEO_COALESCE_MS = 800;

/**
 * The open cut with its history (design music-video b1: Undo, Redo). `revision` goes up with each
 * change of the document; the autosave reads it. `past` and `future` hold whole documents: a cut is
 * small (at most 256 KB), and a stack of documents is the simplest Undo that is always right.
 */
export interface MusicVideoDocState {
  doc: MusicVideoDoc;
  past: MusicVideoDoc[];
  future: MusicVideoDoc[];
  revision: number;
  /** The key and the time of the last change, for `coalesce`. */
  lastKey: string | null;
  lastAt: number;
  /** The limit that the last refused change would pass (the editor shows a note), or null. */
  refused: MusicVideoDocLimit | null;
  /** Goes up with each refused change, so the same note shows again. */
  refusedCount: number;
}

export type MusicVideoDocAction =
  /** One change of the document. Changes with the same `coalesce` key close in time are one Undo step. */
  | { type: 'change'; change: (doc: MusicVideoDoc) => MusicVideoDoc; coalesce?: string; at?: number }
  | { type: 'undo' }
  | { type: 'redo' }
  /**
   * A document from the server (the director wrote the cut, or the newest cut after a stale save).
   * The history starts again. The revision goes up; the editor tells the autosave that the server
   * holds this document (`markSaved`), so it does not go back to the server.
   */
  | { type: 'load'; doc: MusicVideoDoc };

export function initialMusicVideoDocState(doc: MusicVideoDoc): MusicVideoDocState {
  return { doc, past: [], future: [], revision: 0, lastKey: null, lastAt: 0, refused: null, refusedCount: 0 };
}

export function musicVideoDocReducer(state: MusicVideoDocState, action: MusicVideoDocAction): MusicVideoDocState {
  switch (action.type) {
    case 'change': {
      const doc = action.change(state.doc);
      if (doc === state.doc) {
        return state;
      }
      // A change over a stored limit is refused here and never reaches the autosave.
      const refused = docLimitRefusal(state.doc, doc);
      if (refused !== null) {
        return { ...state, refused, refusedCount: state.refusedCount + 1 };
      }
      const at = action.at ?? 0;
      const merges =
        action.coalesce !== undefined &&
        action.coalesce === state.lastKey &&
        at - state.lastAt <= MUSIC_VIDEO_COALESCE_MS;
      return {
        ...state,
        doc,
        past: merges ? state.past : [...state.past, state.doc].slice(-MUSIC_VIDEO_UNDO_STEPS),
        future: [],
        revision: state.revision + 1,
        lastKey: action.coalesce ?? null,
        lastAt: at,
      };
    }
    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (previous === undefined) {
        return state;
      }
      return {
        ...state,
        doc: previous,
        past: state.past.slice(0, -1),
        future: [state.doc, ...state.future],
        revision: state.revision + 1,
        lastKey: null,
        lastAt: 0,
      };
    }
    case 'redo': {
      const [next, ...future] = state.future;
      if (next === undefined) {
        return state;
      }
      return {
        ...state,
        doc: next,
        past: [...state.past, state.doc],
        future,
        revision: state.revision + 1,
        lastKey: null,
        lastAt: 0,
      };
    }
    case 'load':
      return {
        ...state,
        doc: action.doc,
        past: [],
        future: [],
        revision: state.revision + 1,
        lastKey: null,
        lastAt: 0,
      };
    default: {
      const unknown: never = action;
      return unknown;
    }
  }
}
